import { updateCheckoutSession } from '@otter-ai/recall-client';
import {
  CheckoutSessionActionType,
  PurchaseApplyCouponEvent,
  PurchaseCheckoutSessionUpdateEvent,
  trackEvent,
} from '@otter-ai/telemetry';
import { useCheckout } from '@stripe/react-stripe-js';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { couponIsUpdatingAtom } from '#atoms';

type ApplyTrialDiscountParams = {
  couponCode?: string;
  trialDays: number | string;
};

export const useApplyTrialDiscount = () => {
  const { runServerUpdate, id, removePromotionCode } = useCheckout();
  const setIsCouponUpdating = useSetAtom(couponIsUpdatingAtom);

  const applyTrialDiscountMutation = useMutation<
    boolean,
    Error,
    ApplyTrialDiscountParams
  >({
    mutationFn: async ({ couponCode, trialDays }: ApplyTrialDiscountParams) => {
      await runServerUpdate(() =>
        updateCheckoutSession({
          body: {
            checkout_session_id: id,
            session_params: {
              ...(couponCode ? { discounts: [{ coupon: couponCode }] } : {}),
              subscription_data: { trial_period_days: trialDays },
            },
          },
          throwOnError: true,
        }),
      );

      trackEvent(
        PurchaseCheckoutSessionUpdateEvent({
          Status: 'success',
          CheckoutSessionActionType: CheckoutSessionActionType.ChangeTrialDays,
          CouponTrialDays: Number(trialDays),
        }),
      );

      if (!couponCode) {
        const result = await removePromotionCode();
        if (result.type === 'success') {
          return true;
        }
        if (result.type === 'error') {
          throw new Error(result.error.message);
        }
      }

      return true;
    },
    onSuccess: (__, variables) => {
      trackEvent(
        PurchaseApplyCouponEvent({
          CouponCode: variables.couponCode,
          Status: 'success',
        }),
      );
    },
    onError: (error, variables) => {
      trackEvent(
        PurchaseApplyCouponEvent({
          CouponCode: variables.couponCode,
          Status: 'failure',
          ErrorMessage: error.message,
        }),
      );
    },
  });

  useEffect(() => {
    setIsCouponUpdating(applyTrialDiscountMutation.isPending);
  }, [applyTrialDiscountMutation.isPending, setIsCouponUpdating]);

  return applyTrialDiscountMutation;
};
